/**
 * Ryan's Scenarios Dialog
 *
 * Modal dialog for triggering and resetting demo scenarios.
 * Supports both simulation mode (no DB changes) and live mode (real DB changes).
 *
 * NOTE: Formerly the app-bar "Demo Scenarios" dialog. Renamed to "Ryan's
 * Scenarios" and currently hidden from the application bar.
 */

import { useState, useEffect, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Grid2,
  Button,
  Box,
  Paper,
  Alert,
  IconButton,
  Switch,
  FormControlLabel,
  Chip,
  Tabs,
  Tab,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MonitorIcon from '@mui/icons-material/Monitor';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import EditIcon from '@mui/icons-material/Edit';
import HomeIcon from '@mui/icons-material/Home';
import VisibilityIcon from '@mui/icons-material/Visibility';
import InfoIcon from '@mui/icons-material/Info';
import { useApolloClient } from '@apollo/client';

import { ScenarioCard } from './ScenarioCard';
import { ScenarioStatus } from './ScenarioStatus';
import {
  PatientManagementShelf,
  DemoPatient,
} from './PatientManagementShelf';
import { AdmissionCoordinationDialog } from './admission/AdmissionCoordinationDialog';
import { SEARCH_PATIENTS_QUERY } from '~/components/patient/patientSearchQuery';
import {
  getDemoState,
  hasAnyScenarioExecuted,
  getScenarioExecutedAt,
  saveDemoState,
  needsAdmissionCoordination,
  saveAdmissionCoordination,
  getAdmissionCoordination,
  clearAdmissionCoordination,
  markAdmissionScheduled,
} from '~/services/demo/demoStateManager';
import {
  executeAdmissionScenario,
  executePatternChangeScenario,
  executeDischargeScenario,
  cancelVisitsForPatients,
  createAdmissionVisits,
  OrgConfig,
} from '~/services/demo/autoSchedulingDemo';
import {
  simulateAdmissionScenario,
  simulatePatternChangeScenario,
  simulateDischargeScenario,
} from '~/services/demo/simulateAutoSchedulingDemo';
import {
  MOCK_PATIENTS,
  CARE_PATTERNS,
  DEFAULT_LOS_DAYS,
  findAdmissionVisitType,
} from '~/services/demo/mockAutoSchedulingData';
import { useSnackbar } from '~/hooks/useSnackbar';
import { useNavigate } from 'react-router-dom';
import { useOrgConfig } from '~/providers/OrgConfigProvider';
import { PredictDemoContent } from './PredictDemoContent';
import { TransitionDemoContent } from './TransitionDemoContent';
import { CoordinateDemoContent } from './CoordinateDemoContent';

type DemoSection = 'operate' | 'predict' | 'transition' | 'coordinate';
type ScenarioKey = 'scenario1' | 'scenario2' | 'scenario3';

interface ExecutionState {
  isExecuting: boolean;
  currentStep: string;
  steps: string[];
  isComplete: boolean;
  isError: boolean;
  errorMessage?: string;
}

const INITIAL_EXECUTION_STATE: ExecutionState = {
  isExecuting: false,
  currentStep: '',
  steps: [],
  isComplete: false,
  isError: false,
};

export interface RyansScenariosDialogProps {
  open: boolean;
  onClose: () => void;
}

export function RyansScenariosDialog({ open, onClose }: RyansScenariosDialogProps) {
  const { setErrorMessage: showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const orgConfig = useOrgConfig();

  // Simulation mode toggle
  const [isSimulationMode, setIsSimulationMode] = useState(true); // Default to simulation for safety

  // Execution states for each scenario
  const [scenario1State, setScenario1State] = useState<ExecutionState>(INITIAL_EXECUTION_STATE);
  const [scenario2State, setScenario2State] = useState<ExecutionState>(INITIAL_EXECUTION_STATE);
  const [scenario3State, setScenario3State] = useState<ExecutionState>(INITIAL_EXECUTION_STATE);

  // Demo state from localStorage
  const [demoState, setDemoState] = useState(getDemoState());
  const [hasAnyExecuted, setHasAnyExecuted] = useState(hasAnyScenarioExecuted());

  const [activeSection, setActiveSection] = useState<DemoSection>('operate');

  // Patient management
  const [demoPatients, setDemoPatients] = useState<DemoPatient[]>([]);
  const [loadingPatients, setLoadingPatients] = useState(false);

  // Admission coordination dialog
  const [showAdmissionDialog, setShowAdmissionDialog] = useState(false);

  // Refresh demo state
  const refreshDemoState = () => {
    setDemoState(getDemoState());
    setHasAnyExecuted(hasAnyScenarioExecuted());
  };

  // Load demo patients
  const loadDemoPatients = async () => {
    if (isSimulationMode) {
      // In simulation mode, use mock data from state
      const state = getDemoState();
      const patients: DemoPatient[] = [];

      if (state.scenario1?.patientDetails) {
        patients.push({
          patientId: state.scenario1.patientId,
          firstName: state.scenario1.patientDetails.firstName,
          lastName: state.scenario1.patientDetails.lastName,
          mrn: state.scenario1.patientDetails.mrn,
          episodeCount: state.scenario1.episodeDetails ? 1 : 0,
          visitCount: state.scenario1.visits?.length || 0,
          activeVisitCount: state.scenario1.visits?.filter((v) => v.eventStatus !== 'canceled').length || 0,
        });
      }

      setDemoPatients(patients);
      return;
    }

    // In live mode, query database for demo patients
    setLoadingPatients(true);
    try {
      const result = await apolloClient.query({
        query: SEARCH_PATIENTS_QUERY,
        variables: {
          searchTerm: '-AS',
          limit: 50,
          offset: 0,
        },
        fetchPolicy: 'network-only',
      });

      const patients: DemoPatient[] = result.data.searchPatient
        .filter((sp: any) => {
          // Only include patients with active episodes (proxy for having uncancelled visits)
          return sp.bestEpisode?.active === true;
        })
        .map((sp: any) => {
          const mrn = sp.patient.patientIdentifiers?.find((pi: any) => pi.identifier_type === 'mrn')?.identifier || '';
          return {
            patientId: sp.patient.id,
            firstName: sp.patient.firstName,
            lastName: sp.patient.lastName,
            mrn,
            episodeCount: sp.bestEpisode ? 1 : 0,
            visitCount: 0, // We don't have visit count from SearchPatients
            activeVisitCount: 0, // We'll show all patients with active episodes
          };
        });

      setDemoPatients(patients);
    } catch (error: any) {
      console.error('Failed to load demo patients:', error);
      showSnackbar('Failed to load demo patients', 'error');
    } finally {
      setLoadingPatients(false);
    }
  };

  // Load patients when dialog opens or mode changes
  useEffect(() => {
    if (open) {
      loadDemoPatients();
    }
  }, [open, hasAnyExecuted, isSimulationMode]);

  // Clear admission coordination when switching to simulation mode
  useEffect(() => {
    if (isSimulationMode) {
      console.log('[RyansScenariosDialog] Simulation mode active - clearing admission coordination');
      clearAdmissionCoordination();
    }
  }, [isSimulationMode]);

  // Scenario 1: Admission Order
  const handleTriggerScenario1 = async () => {
    // Clear any existing admission coordination state if in simulation mode
    if (isSimulationMode) {
      console.log('[Demo Scenario 1] Simulation mode - clearing any existing admission coordination');
      clearAdmissionCoordination();
    }

    // Check if org config is loaded
    if (!isSimulationMode && orgConfig.loading) {
      showSnackbar('Organization config is still loading. Please wait...', 'warning');
      return;
    }

    const steps = [
      'Simulating EHR order...',
      'Creating patient...',
      'Activating episode...',
    ];

    setScenario1State({
      isExecuting: true,
      currentStep: steps[0],
      steps,
      isComplete: false,
      isError: false,
    });

    // Choose simulation or real execution
    const result = isSimulationMode
      ? await simulateAdmissionScenario(
          MOCK_PATIENTS[0],
          CARE_PATTERNS.standard,
          DEFAULT_LOS_DAYS,
          (step) => {
            setScenario1State((prev) => ({
              ...prev,
              currentStep: step,
            }));
          }
        )
      : await executeAdmissionScenario(
          apolloClient,
          orgConfig as OrgConfig,
          MOCK_PATIENTS[0],
          CARE_PATTERNS.standard,
          DEFAULT_LOS_DAYS,
          (step) => {
            setScenario1State((prev) => ({
              ...prev,
              currentStep: step,
            }));
          }
        );

    if (result.success) {
      console.log('[Demo Scenario 1] Success! Result:', result);

      setScenario1State((prev) => ({
        ...prev,
        isExecuting: false,
        isComplete: true,
      }));
      refreshDemoState();
      await loadDemoPatients();

      // Refetch queries to update the UI
      console.log('[Demo Scenario 1] Refetching queries...');
      await apolloClient.refetchQueries({
        include: ['ListActivePatients', 'CountUnscheduledAdmissions'],
      });
      console.log('[Demo Scenario 1] Queries refetched');

      // Initialize admission coordination state with patient details but don't open dialog
      // Only save coordination state in LIVE mode - simulation mode just shows the outcome
      if (!isSimulationMode && result.patientId && result.episodeId && result.patientDetails) {
        // Use patient details directly from the execution result
        const { firstName, lastName, mrn } = result.patientDetails;

        console.log('[Demo Scenario 1] Saving admission coordination for:', firstName, lastName);

        saveAdmissionCoordination({
          needsCoordination: true,
          patientId: result.patientId,
          episodeId: result.episodeId,
          patientName: `${firstName} ${lastName}`,
          mrn: mrn,
          admissionOrderTime: new Date().toISOString(),
          diagnosis: 'CHF exacerbation',
          expectedLOS: DEFAULT_LOS_DAYS,
          supplies: {
            vendor: null,
            ordered: false,
            estimatedDelivery: null,
            items: [],
          },
          transportation: {
            ordered: false,
            estimatedArrival: null,
            duration: 0,
          },
          recommendedTime: null,
          completed: false,
        });
      }

      const patientName = result.patientDetails
        ? `${result.patientDetails.firstName} ${result.patientDetails.lastName}`
        : 'New patient';

      const message = `🚨 NEW ADMISSION ORDER! Patient ${patientName} added to census.`;

      console.log('[Demo Scenario 1] About to show snackbar with message:', message);

      // Show notification with action to view patient
      showSnackbar(message, 'info', {
        label: 'View Patient',
        onClick: () => {
          navigate(`/patients/${result.patientId}`);
        },
      });
    } else {
      setScenario1State((prev) => ({
        ...prev,
        isExecuting: false,
        isError: true,
        errorMessage: result.error,
      }));
      showSnackbar(`Admission scenario failed: ${result.error}`, 'error');
    }
  };

  // Scenario 2: Pattern Change Order
  const handleTriggerScenario2 = async () => {
    const state = getDemoState();
    if (!state.scenario1) {
      showSnackbar('Please execute Scenario 1 (Admission Order) first', 'error');
      return;
    }

    const admissionDateStr =
      state.scenario1.episodeDetails?.admissionDate || new Date().toISOString();
    const expectedLOS =
      state.scenario1.episodeDetails?.expectedLOS || DEFAULT_LOS_DAYS;

    const steps = [
      'Simulating EHR order...',
      'Canceling future visits...',
      'Updating pattern of care...',
      'Scheduling new visits...',
    ];

    setScenario2State({
      isExecuting: true,
      currentStep: steps[0],
      steps,
      isComplete: false,
      isError: false,
    });

    const result = isSimulationMode
      ? await simulatePatternChangeScenario(
          state.scenario1.patientId,
          state.scenario1.episodeId,
          admissionDateStr,
          expectedLOS,
          (step) => {
            setScenario2State((prev) => ({
              ...prev,
              currentStep: step,
            }));
          }
        )
      : await executePatternChangeScenario(
          apolloClient,
          orgConfig as OrgConfig,
          state.scenario1.patientId,
          state.scenario1.episodeId,
          admissionDateStr,
          expectedLOS,
          (step) => {
            setScenario2State((prev) => ({
              ...prev,
              currentStep: step,
            }));
          }
        );

    if (result.success) {
      setScenario2State((prev) => ({
        ...prev,
        isExecuting: false,
        isComplete: true,
      }));
      refreshDemoState();
      await loadDemoPatients();

      await apolloClient.refetchQueries({
        include: ['ListActivePatients', 'CountUnscheduledAdmissions'],
      });

      const { patientName, canceledCount, newCount } = result as any;
      const toastMsg = patientName
        ? `${patientName}'s care pattern updated — ${canceledCount ?? 0} visits canceled, ${newCount ?? 0} new visits scheduled`
        : 'Care pattern updated successfully';
      showSnackbar(toastMsg, 'success');
    } else {
      setScenario2State((prev) => ({
        ...prev,
        isExecuting: false,
        isError: true,
        errorMessage: result.error,
      }));
      showSnackbar(`Pattern change scenario failed: ${result.error}`, 'error');
    }
  };

  // Scenario 3: Discharge Order
  const handleTriggerScenario3 = async () => {
    const state = getDemoState();
    if (!state.scenario1) {
      showSnackbar('Please execute Scenario 1 (Admission Order) first', 'error');
      return;
    }

    const steps = [
      'Simulating EHR discharge order...',
      'Canceling remaining routine visits...',
      'Scheduling discharge day visits...',
    ];

    setScenario3State({
      isExecuting: true,
      currentStep: steps[0],
      steps,
      isComplete: false,
      isError: false,
    });

    const dischargeDate = new Date();
    dischargeDate.setDate(dischargeDate.getDate() + 1);

    const result = isSimulationMode
      ? await simulateDischargeScenario(
          state.scenario1.patientId,
          state.scenario1.episodeId,
          dischargeDate,
          (step) => {
            setScenario3State((prev) => ({
              ...prev,
              currentStep: step,
            }));
          }
        )
      : await executeDischargeScenario(
          apolloClient,
          orgConfig as OrgConfig,
          state.scenario1.patientId,
          state.scenario1.episodeId,
          dischargeDate,
          (step) => {
            setScenario3State((prev) => ({
              ...prev,
              currentStep: step,
            }));
          }
        );

    if (result.success) {
      setScenario3State((prev) => ({
        ...prev,
        isExecuting: false,
        isComplete: true,
      }));
      refreshDemoState();
      await loadDemoPatients();

      // Refetch queries to update the UI
      await apolloClient.refetchQueries({
        include: ['ListActivePatients', 'CountUnscheduledAdmissions'],
      });

      const { patientName: resultPatientName, canceledCount, dischargeDate } = result as any;
      const patientDetails = state.scenario1.patientDetails;
      const patientName = resultPatientName
        || (patientDetails ? `${patientDetails.firstName} ${patientDetails.lastName}` : null);
      const dischargeDateLabel = dischargeDate
        ? new Date(dischargeDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        : 'tomorrow';
      const toastMsg = `🏠 Discharge order received${patientName ? ` for ${patientName}` : ''} — ${canceledCount ?? 0} routine visits canceled, discharge day scheduled for ${dischargeDateLabel}`;
      showSnackbar(toastMsg, 'success', {
        label: 'View Patient',
        onClick: () => navigate(`/patients/${result.patientId}`),
      });
    } else {
      setScenario3State((prev) => ({
        ...prev,
        isExecuting: false,
        isError: true,
        errorMessage: result.error,
      }));
      showSnackbar(`Discharge scenario failed: ${result.error}`, 'error');
    }
  };

  // Handle cancel visits for patients
  const handleCancelPatientVisits = async (patientIds: number[]) => {
    // Check if any of the canceled patients have pending admission coordination
    const admissionCoord = getAdmissionCoordination();
    if (admissionCoord && patientIds.includes(admissionCoord.patientId)) {
      clearAdmissionCoordination();
      console.log('[handleCancelPatientVisits] Cleared admission coordination for patient', admissionCoord.patientId);
    }

    if (isSimulationMode) {
      // In simulation mode, update the stored state to mark visits as canceled
      const state = getDemoState();
      let visitsCanceled = 0;

      // Update each scenario's visits
      if (state.scenario1 && patientIds.includes(state.scenario1.patientId)) {
        const updatedVisits = state.scenario1.visits?.map((v) => ({
          ...v,
          eventStatus: 'canceled',
        }));
        const updatedState = {
          ...state.scenario1,
          visits: updatedVisits,
        };
        saveDemoState('scenario1', updatedState);
        visitsCanceled += updatedVisits?.length || 0;
      }

      showSnackbar(
        `Canceled ${visitsCanceled} visit${visitsCanceled !== 1 ? 's' : ''} for ${patientIds.length} patient${patientIds.length !== 1 ? 's' : ''} (simulation mode)`,
        'success'
      );
      await loadDemoPatients();

      // Refetch queries to update the UI
      await apolloClient.refetchQueries({
        include: ['ListActivePatients', 'CountUnscheduledAdmissions'],
      });

      return;
    }

    // In live mode, cancel visits in the database
    try {
      await cancelVisitsForPatients(
        apolloClient,
        orgConfig as OrgConfig,
        patientIds,
        (step) => console.log('Cancel progress:', step)
      );
      showSnackbar(`Successfully canceled visits for ${patientIds.length} patient(s)`, 'success');
      await loadDemoPatients();

      // Refetch queries to update the UI
      await apolloClient.refetchQueries({
        include: ['ListActivePatients', 'CountUnscheduledAdmissions'],
      });
    } catch (error: any) {
      console.error('Failed to cancel patient visits:', error);
      throw error; // Re-throw so the shelf can handle it
    }
  };

  // View patient profile handler
  const handleViewPatient = (patientId: number) => {
    if (isSimulationMode) {
      navigate(`/demo-patients/${patientId}`);
    } else {
      navigate(`/patients/${patientId}`);
    }
  };

  // Handle admission coordination complete
  const handleAdmissionCoordinationComplete = async (data: {
    admissionTime: string;
    providerResources: Array<{ providerId: number; providerTypeId?: number }>;
    autoScheduleToday: boolean;
    selectedVisitTypeId: number | null;
  }) => {
    const coordination = getAdmissionCoordination();
    if (!coordination) {
      showSnackbar('Admission coordination data not found', 'error');
      return;
    }

    try {
      if (isSimulationMode) {
        // In simulation mode, just update state
        showSnackbar('Admission visit scheduled! (Simulation mode)', 'success');
        setShowAdmissionDialog(false);
        return;
      }

      // In live mode, create actual visits
      const serviceLineId = Object.values(orgConfig.serviceLines)[0]?.id;

      if (!serviceLineId) {
        throw new Error('Service line not found');
      }

      const { admissionVisitId, todaysVisitIds } = await createAdmissionVisits(
        apolloClient,
        orgConfig as OrgConfig,
        coordination.patientId,
        coordination.episodeId,
        serviceLineId,
        data.admissionTime,
        data.providerResources,
        data.autoScheduleToday,
        CARE_PATTERNS.standard,
        data.selectedVisitTypeId,
        undefined, // onProgress
        coordination.expectedLOS // Pass expected LOS from coordination state
      );

      markAdmissionScheduled(coordination.episodeId);

      const expectedLOS = coordination.expectedLOS || DEFAULT_LOS_DAYS;
      const futureVisits = (expectedLOS - 1) * CARE_PATTERNS.standard.timeWindows.length;
      const totalVisits = 1 + todaysVisitIds.length + futureVisits;
      showSnackbar(
        `Episode scheduled! ${totalVisits} visits created across ${expectedLOS} days of care.`,
        'success'
      );
      setShowAdmissionDialog(false);
      await loadDemoPatients();

      // Refetch queries to update the UI
      await apolloClient.refetchQueries({
        include: ['ListActivePatients', 'CountUnscheduledAdmissions'],
      });
    } catch (error: any) {
      console.error('Failed to create admission visits:', error);
      showSnackbar(`Failed to create visits: ${error.message}`, 'error');
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { minHeight: '80vh' }
        }}
      >
        <DialogTitle sx={{ pb: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h5" component="span">
                Ryan's Scenarios
              </Typography>
              {activeSection === 'operate' && isSimulationMode && (
                <Chip
                  icon={<InfoIcon />}
                  label="Simulation Mode"
                  color="info"
                  size="small"
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {activeSection === 'operate' && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={isSimulationMode}
                      onChange={(e) => setIsSimulationMode(e.target.checked)}
                      disabled={scenario1State.isExecuting || scenario2State.isExecuting || scenario3State.isExecuting}
                    />
                  }
                  label={
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" fontWeight={600}>
                        {isSimulationMode ? 'Simulation' : 'Live'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {isSimulationMode ? 'No DB changes' : 'Real data'}
                      </Typography>
                    </Box>
                  }
                  labelPlacement="start"
                />
              )}
              <IconButton onClick={onClose} edge="end">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          <Tabs
            value={activeSection}
            onChange={(_, v) => setActiveSection(v as DemoSection)}
          >
            <Tab label="Operate" value="operate" sx={{ textTransform: 'none' }} />
            <Tab label="Predict" value="predict" sx={{ textTransform: 'none' }} />
            <Tab label="Coordinate" value="coordinate" sx={{ textTransform: 'none' }} />
            <Tab label="Transition" value="transition" sx={{ textTransform: 'none' }} />
          </Tabs>
        </DialogTitle>

        <DialogContent>
          {activeSection === 'predict' && <PredictDemoContent />}
          {activeSection === 'coordinate' && <CoordinateDemoContent />}
          {activeSection === 'transition' && <TransitionDemoContent />}
          {activeSection === 'operate' && <>
          {/* Info Alert */}
          <Alert severity={isSimulationMode ? 'success' : 'info'} sx={{ mb: 3 }}>
            {isSimulationMode ? (
              <>
                <strong>Simulation Mode Active:</strong> No real data will be created in the database.
                All scenarios are simulated with mock data for demonstration purposes only.
              </>
            ) : (
              <>
                <strong>Live Mode Active:</strong> These scenarios will create real patients, episodes, and visits in your database.
                Each run creates a unique patient with a timestamped MRN. Use the Patient Management section below to cancel visits as needed.
              </>
            )}
            {' '}Scenarios 2 and 3 require Scenario 1 to be executed first.
          </Alert>

          {/* Scenario Cards */}
          <Grid2 container spacing={3} sx={{ alignItems: 'stretch' }}>
            {/* Scenario 1 */}
            <Grid2 size={{ xs: 12, md: 4 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <ScenarioCard
                  title="Scenario 1: Admission Order"
                  description="Simulates an admission order from the EHR. Creates patient, activates episode, and auto-schedules all visits for the expected length of stay."
                  icon={<LocalHospitalIcon sx={{ fontSize: 40 }} />}
                  onTrigger={handleTriggerScenario1}
                  isExecuting={scenario1State.isExecuting}
                  isResetting={false}
                  hasBeenExecuted={!!demoState.scenario1}
                  executedAt={getScenarioExecutedAt('scenario1')}
                  color="primary"
                />
                {scenario1State.isExecuting && (
                  <Paper sx={{ mt: 2, p: 2 }}>
                    <ScenarioStatus
                      steps={scenario1State.steps}
                      currentStep={scenario1State.currentStep}
                      isComplete={scenario1State.isComplete}
                      isError={scenario1State.isError}
                      errorMessage={scenario1State.errorMessage}
                    />
                  </Paper>
                )}
                {isSimulationMode && demoState.scenario1 && (
                  <Button
                    variant="outlined"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handleViewPatient(demoState.scenario1!.patientId)}
                    fullWidth
                    sx={{ mt: 2 }}
                  >
                    View Patient Profile
                  </Button>
                )}
              </Box>
            </Grid2>

            {/* Scenario 2 */}
            <Grid2 size={{ xs: 12, md: 4 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <ScenarioCard
                  title="Scenario 2: Pattern Change Order"
                  description="Simulates a new physician order that changes the pattern of care. Cancels future visits and auto-schedules new visits with updated pattern."
                  icon={<EditIcon sx={{ fontSize: 40 }} />}
                  onTrigger={handleTriggerScenario2}
                  isExecuting={scenario2State.isExecuting}
                  isResetting={false}
                  hasBeenExecuted={!!demoState.scenario2}
                  executedAt={getScenarioExecutedAt('scenario2')}
                  disabled={!demoState.scenario1}
                  color="secondary"
                />
                {scenario2State.isExecuting && (
                  <Paper sx={{ mt: 2, p: 2 }}>
                    <ScenarioStatus
                      steps={scenario2State.steps}
                      currentStep={scenario2State.currentStep}
                      isComplete={scenario2State.isComplete}
                      isError={scenario2State.isError}
                      errorMessage={scenario2State.errorMessage}
                    />
                  </Paper>
                )}
                {isSimulationMode && demoState.scenario2 && (
                  <Button
                    variant="outlined"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handleViewPatient(demoState.scenario2!.patientId)}
                    fullWidth
                    sx={{ mt: 2 }}
                  >
                    View Patient Profile
                  </Button>
                )}
              </Box>
            </Grid2>

            {/* Scenario 3 */}
            <Grid2 size={{ xs: 12, md: 4 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <ScenarioCard
                  title="Scenario 3: Discharge Order"
                  description="Simulates a discharge order 24 hours in advance. Updates final day pattern to include routine visit and discharge visit."
                  icon={<HomeIcon sx={{ fontSize: 40 }} />}
                  onTrigger={handleTriggerScenario3}
                  isExecuting={scenario3State.isExecuting}
                  isResetting={false}
                  hasBeenExecuted={!!demoState.scenario3}
                  executedAt={getScenarioExecutedAt('scenario3')}
                  disabled={!demoState.scenario1}
                  color="info"
                />
                {scenario3State.isExecuting && (
                  <Paper sx={{ mt: 2, p: 2 }}>
                    <ScenarioStatus
                      steps={scenario3State.steps}
                      currentStep={scenario3State.currentStep}
                      isComplete={scenario3State.isComplete}
                      isError={scenario3State.isError}
                      errorMessage={scenario3State.errorMessage}
                    />
                  </Paper>
                )}
                {isSimulationMode && demoState.scenario3 && (
                  <Button
                    variant="outlined"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handleViewPatient(demoState.scenario3!.patientId)}
                    fullWidth
                    sx={{ mt: 2 }}
                  >
                    View Patient Profile
                  </Button>
                )}
              </Box>
            </Grid2>
          </Grid2>

          {/* EHR Integration Demo */}
          <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="subtitle2" fontWeight={700}>EHR Integration Demo</Typography>
              <Typography variant="body2" color="text.secondary">
                Mock Epic UI demonstrating MedArrive integration from the Virtual CCN Nurse's perspective.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<MonitorIcon />}
              onClick={() => { onClose(); navigate('/ehr-demo'); }}
              sx={{ ml: 2, whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              View EHR Demo
            </Button>
          </Box>

          {/* Patient Management Shelf */}
          {(hasAnyExecuted || (!isSimulationMode && demoPatients.length > 0)) && (
            <Box sx={{ mt: 4 }}>
              <PatientManagementShelf
                patients={demoPatients}
                onCancelAllVisits={handleCancelPatientVisits}
                onViewPatient={handleViewPatient}
                loading={loadingPatients}
                disabled={scenario1State.isExecuting || scenario2State.isExecuting || scenario3State.isExecuting}
              />
            </Box>
          )}
          </>}
        </DialogContent>
      </Dialog>

      {/* Admission Coordination Dialog */}
      <AdmissionCoordinationDialog
        open={showAdmissionDialog}
        onClose={() => setShowAdmissionDialog(false)}
        onComplete={handleAdmissionCoordinationComplete}
        isSimulationMode={isSimulationMode}
        admissionVisitType={
          !orgConfig.loading
            ? orgConfig.visitTypes[findAdmissionVisitType(orgConfig.visitTypes) || 0] || null
            : null
        }
      />
    </>
  );
}
